/* eslint-disable react-hooks/exhaustive-deps */
import {
  ActionCardProps,
  ActionsProps,
  EnquiryHandle,
  Product,
  RightPanelTabs,
  RightPanelTabsConfiguration,
} from '@access-erp/common';

import { CardType, IRowNode } from '@access-erp/ui-components';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { UserRole } from '@/config/roles.config';
import { useResendSalesOrdersByBillingCycles } from './useResendSalesOrdersByBillingCycles';
import { billingBCPath } from '@/utils/path.utlis';
import { SALES_ORDERS_STATUSES_PASCAL_CASE_MAP } from '@/config/statuses.config';
import { SalesOrderStatusEnum, SalesOrderTypeEnum } from '@/graphql/contracts/_generated_/contracts';
import { EnvUtils } from '@/utils/env.utils';
import { FAILED_SO_STATUSES } from '../columns/useDefaultGridFilter';

import { SalesOrderWithId } from '@/utils/data-helpers/types';

type ActionButton = {
  config: ActionCardProps;
  isVisible: boolean;
};

interface UseRightPanelParams {
  selectedHeaderRows: IRowNode<SalesOrderWithId>[];
  gridRef?: EnquiryHandle<SalesOrderWithId>;
  userRole: UserRole;
}

export enum SOActionEnum {
  RESEND = 'resend',
  ABANDON = 'abandon',
}

export function useRightPanel({ selectedHeaderRows, gridRef, userRole }: UseRightPanelParams) {
  const { resendSalesOrdersByBillingCycles } = useResendSalesOrdersByBillingCycles();
  const [isVisiblePredictionModal, setIsVisiblePredictionModal] = useState(false);
  const [isVisibleActionConfirmModal, setIsVisibleActionConfirmModal] = useState(false);
  const [isVisibleSOPayloadModal, setIsVisibleSOPayloadModal] = useState(false);
  const [isVisibleAbandonModal, setIsVisibleAbandonModal] = useState(false);
  const [action, setAction] = useState<SOActionEnum>();
  const { pathname } = useLocation();
  const isBillingCyclesPath = pathname.includes(billingBCPath);

  const salesOrdersAreResendable = useMemo(() => {
    return (
      selectedHeaderRows.length > 0 &&
      selectedHeaderRows.every((so: IRowNode<SalesOrderWithId>) =>
        FAILED_SO_STATUSES.includes(so?.data?.status?.formattedStatus ?? ''),
      )
    );
  }, [selectedHeaderRows]);

  const isFullAccessOrAdminRole = userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN;

  const selectedSalesOrder = useMemo(() => {
    if (selectedHeaderRows.length !== 1) return null;
    return selectedHeaderRows[0]?.data ?? null;
  }, [selectedHeaderRows]);

  const isSelectedSOAbandoned = useMemo(() => {
    return selectedSalesOrder?.isAbandoned === true;
  }, [selectedSalesOrder]);

  const canAbandonOrReinstateSalesOrder = useMemo(() => {
    if (!EnvUtils.isSelectiveBillingEnabled()) {
      return false;
    }
    if (selectedHeaderRows.length !== 1 || !isBillingCyclesPath || !isFullAccessOrAdminRole) {
      return false;
    }
    const selectedSO = selectedHeaderRows[0];
    const status = selectedSO?.data?.status?.status;
    const isAbandoned = selectedSO?.data?.isAbandoned;
      
    if (selectedSO?.data?.type?.toLocaleLowerCase() == SalesOrderTypeEnum.PROFORMA.toLocaleLowerCase()) {
      return false;
    }

    if (isAbandoned) {
      return true;
    }
      
    return status !== SALES_ORDERS_STATUSES_PASCAL_CASE_MAP[SalesOrderStatusEnum.SENT];
  }, [selectedHeaderRows, isBillingCyclesPath, isFullAccessOrAdminRole]);

  const handleSuccessAction = () => {
    gridRef?.deselectRows();
    gridRef?.refreshServerSideGrid({
      purge: true,
    });
  };

  const confirmResendSalesOrders = () => {
    const salesOrderIds: string[] = selectedHeaderRows
      .map((salesOrder) => salesOrder.data?.salesOrderID)
      .filter((id): id is string => typeof id === 'string');
    resendSalesOrdersByBillingCycles({ salesOrderIds, onSuccess: handleSuccessAction });
    setIsVisibleActionConfirmModal(false);
  };

  const abandonButtonText = isSelectedSOAbandoned ? 'Reinstate Sales Order' : 'Abandon Sales Order';

  const actionButtons = useMemo<ActionButton[]>(
    () => [
      ...(isFullAccessOrAdminRole
        ? [
          {
            config: {
              text: 'Resend failed SO',
              cardType: CardType.LINK,
              product: Product.ContractsAndBilling,
              onClick: () => {
                setIsVisibleActionConfirmModal(true);
                setAction(SOActionEnum.RESEND);
              },
            },
            isVisible: salesOrdersAreResendable && isBillingCyclesPath,
          },
          {
            config: {
              text: 'Sales Orders Predictions vs Actual',
              cardType: CardType.ACTION,
              product: Product.ContractsAndBilling,
              onClick: () => setIsVisiblePredictionModal(true),
            },
            isVisible: isBillingCyclesPath,
          },
        ]
        : []),
      {
        config: {
          text: 'Sales Order Payload',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: () => {
            setIsVisibleSOPayloadModal(true);
          },
        },
        isVisible: selectedHeaderRows.length == 1,
      },
      {
        config: {
          text: abandonButtonText,
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: () => {
            setIsVisibleAbandonModal(true);
          },
        },
        isVisible: canAbandonOrReinstateSalesOrder,
      },
    ],
    [selectedHeaderRows, 
      canAbandonOrReinstateSalesOrder, 
      abandonButtonText, 
      isBillingCyclesPath],
  );

  const actionsParams: ActionsProps = {
    actionCards: {
      actions: actionButtons.filter((el) => el.isVisible).map((el) => el.config),
    },
  };

  const tabsConfiguration = useMemo<RightPanelTabsConfiguration>(
    () => ({
      [RightPanelTabs.ACTIONS]: actionsParams,
    }),
    [selectedHeaderRows],
  );

  return {
    tabsConfiguration,
    closePredictionTableModal: () => setIsVisiblePredictionModal(false),
    closeActionConfirmModal: () => setIsVisibleActionConfirmModal(false),
    confirmResendSalesOrders,
    isVisiblePredictionModal,
    action,
    isVisibleActionConfirmModal,
    isVisibleSOPayloadModal,
    setIsVisibleSOPayloadModal,
    isVisibleAbandonModal,
    setIsVisibleAbandonModal,
    handleSuccessAction,
    selectedSalesOrder,
  };
}
